import { useEffect, useState } from 'react';
import type {
  AdminMaintenanceStatus,
  AdminUserResource,
  SystemMediaStorageStatus,
} from '../../shared/admin';
import { ApiRequestError } from '../auth/authApi';
import {
  getAdminMaintenance,
  getSystemMediaStorage,
  listAdminUsers,
  runAdminCleanup,
  updateSystemMediaLimit,
  updateUserResourceLimits,
} from './adminApi';

const BYTES_PER_MIB = 1024 * 1024;
const BYTES_PER_GIB = 1024 * BYTES_PER_MIB;

function formatStorage(bytes: number): string {
  if (bytes < BYTES_PER_MIB) return `${Math.max(0, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * BYTES_PER_MIB) {
    return `${(bytes / BYTES_PER_MIB).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} MB`;
  }
  return `${(bytes / (1024 * BYTES_PER_MIB)).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} GB`;
}

function formatDate(timestamp: number | null): string {
  return timestamp ? new Date(timestamp).toLocaleString('tr-TR') : 'Henüz tamamlanmadı';
}

export function AdminPanel({ onQuotaChanged }: { onQuotaChanged?: () => void }) {
  const [users, setUsers] = useState<AdminUserResource[]>([]);
  const [mediaValues, setMediaValues] = useState<Record<string, string>>({});
  const [publicationValues, setPublicationValues] = useState<Record<string, string>>({});
  const [defaultMediaLimitBytes, setDefaultMediaLimitBytes] = useState(50 * BYTES_PER_MIB);
  const [defaultPublicationLimit, setDefaultPublicationLimit] = useState(25);
  const [maximumMediaLimitBytes, setMaximumMediaLimitBytes] = useState(50 * 1024 * BYTES_PER_MIB);
  const [maintenance, setMaintenance] = useState<AdminMaintenanceStatus | null>(null);
  const [systemStorage, setSystemStorage] = useState<SystemMediaStorageStatus | null>(null);
  const [systemLimitGib, setSystemLimitGib] = useState('10');
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [savingSystemLimit, setSavingSystemLimit] = useState(false);
  const [message, setMessage] = useState('');

  const mergeUsers = (nextUsers: AdminUserResource[], append = false) => {
    setUsers((current) => append ? [...current, ...nextUsers] : nextUsers);
    setMediaValues((current) => {
      const next = append ? { ...current } : {};
      for (const user of nextUsers) next[user.id] = String(Math.round(user.mediaLimitBytes / BYTES_PER_MIB));
      return next;
    });
    setPublicationValues((current) => {
      const next = append ? { ...current } : {};
      for (const user of nextUsers) next[user.id] = String(user.activePublicationLimit);
      return next;
    });
  };

  useEffect(() => {
    listAdminUsers()
      .then((userData) => {
        mergeUsers(userData.users);
        setDefaultMediaLimitBytes(userData.defaultMediaLimitBytes);
        setDefaultPublicationLimit(userData.defaultActivePublicationLimit);
        setMaximumMediaLimitBytes(userData.maximumMediaLimitBytes);
        setHasMore(userData.hasMore);
      })
      .catch((error) => setMessage(error instanceof ApiRequestError ? error.message : 'Yönetici verileri yüklenemedi.'))
      .finally(() => setLoading(false));
    getAdminMaintenance()
      .then(setMaintenance)
      .catch((error) => setMessage(error instanceof ApiRequestError ? error.message : 'Proje alanı ve temizlik durumu yüklenemedi.'));
    getSystemMediaStorage()
      .then((storage) => {
        setSystemStorage(storage);
        setSystemLimitGib(String(Number((storage.limitBytes / BYTES_PER_GIB).toFixed(2))));
        setMaximumMediaLimitBytes(storage.limitBytes);
      })
      .catch((error) => setMessage(error instanceof ApiRequestError ? error.message : 'Sistem görsel kotası yüklenemedi.'));
  }, []);

  const replaceUser = (updated: AdminUserResource) => {
    setUsers((current) => current.map((user) => user.id === updated.id ? updated : user));
    setMediaValues((current) => ({
      ...current,
      [updated.id]: String(Math.round(updated.mediaLimitBytes / BYTES_PER_MIB)),
    }));
    setPublicationValues((current) => ({
      ...current,
      [updated.id]: String(updated.activePublicationLimit),
    }));
  };

  const save = async (
    user: AdminUserResource,
    reset: 'none' | 'media' | 'publications' = 'none',
  ) => {
    const mediaMib = Number(mediaValues[user.id]);
    const publicationLimit = Number(publicationValues[user.id]);
    if (reset !== 'media' && (!Number.isInteger(mediaMib) || mediaMib < 1 || mediaMib > maximumMediaLimitBytes / BYTES_PER_MIB)) {
      setMessage(`Görsel kotası 1 ile ${Math.floor(maximumMediaLimitBytes / BYTES_PER_MIB).toLocaleString('tr-TR')} MiB arasında tam sayı olmalıdır.`);
      return;
    }
    if (reset !== 'publications' && (!Number.isInteger(publicationLimit) || publicationLimit < 1 || publicationLimit > 10_000)) {
      setMessage('Aktif yayın kotası 1 ile 10.000 arasında tam sayı olmalıdır.');
      return;
    }
    const mediaLimitBytes = reset === 'media'
      ? null
      : user.hasCustomMediaLimit || reset === 'none' ? mediaMib * BYTES_PER_MIB : null;
    const activePublicationLimit = reset === 'publications'
      ? null
      : user.hasCustomActivePublicationLimit || reset === 'none' ? publicationLimit : null;
    setSavingUserId(user.id);
    setMessage('');
    try {
      const updated = await updateUserResourceLimits(user.id, mediaLimitBytes, activePublicationLimit);
      replaceUser(updated);
      onQuotaChanged?.();
      setMessage(`${user.email} için kaynak kotaları güncellendi.`);
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Kotalar güncellenemedi.');
    } finally {
      setSavingUserId(null);
    }
  };

  const startCleanup = async () => {
    setCleaning(true);
    setMessage('Süresi dolmuş yayınlar ve sonuçlar güvenli biçimde temizleniyor…');
    try {
      const cleanup = await runAdminCleanup();
      const nextMaintenance = await getAdminMaintenance();
      setMaintenance({ ...nextMaintenance, cleanup });
      setMessage(`Temizlik tamamlandı: ${cleanup.lastDeletedPublications} yayın ve ${cleanup.lastDeletedResults} sonuç kaldırıldı.`);
      onQuotaChanged?.();
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Temizlik tamamlanamadı.');
    } finally {
      setCleaning(false);
    }
  };

  const loadMore = async () => {
    setLoading(true);
    try {
      const data = await listAdminUsers(users.length);
      mergeUsers(data.users, true);
      setHasMore(data.hasMore);
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Kullanıcılar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const saveSystemLimit = async () => {
    const gib = Number(systemLimitGib.replace(',', '.'));
    if (!Number.isFinite(gib) || gib <= 0) {
      setMessage('Sistem görsel kotası sıfırdan büyük bir GB değeri olmalıdır.');
      return;
    }
    const limitBytes = Math.round(gib * BYTES_PER_GIB);
    const belowUsage = Boolean(systemStorage && limitBytes < systemStorage.totalMediaBytes);
    if (belowUsage && !window.confirm(
      'Yeni kota mevcut görsel kullanımının altında. Mevcut dosyalar silinmez ancak tüm yeni görsel yüklemeleri hemen durur. Devam etmek istiyor musun?',
    )) {
      return;
    }
    setSavingSystemLimit(true);
    setMessage('Sistem görsel kotası güncelleniyor…');
    try {
      const storage = await updateSystemMediaLimit(limitBytes, belowUsage);
      setSystemStorage(storage);
      setMaximumMediaLimitBytes(storage.limitBytes);
      setSystemLimitGib(String(Number((storage.limitBytes / BYTES_PER_GIB).toFixed(2))));
      const userData = await listAdminUsers();
      mergeUsers(userData.users);
      setDefaultMediaLimitBytes(userData.defaultMediaLimitBytes);
      setDefaultPublicationLimit(userData.defaultActivePublicationLimit);
      setHasMore(userData.hasMore);
      onQuotaChanged?.();
      setMessage('Platformun toplam görsel kotası güncellendi.');
    } catch (error) {
      setMessage(error instanceof ApiRequestError ? error.message : 'Sistem görsel kotası güncellenemedi.');
    } finally {
      setSavingSystemLimit(false);
    }
  };

  const storagePercent = maintenance
    ? Math.min(100, (maintenance.storage.projectDirectoryBytes / maintenance.storage.filesystemCapacityBytes) * 100)
    : 0;
  const systemMediaPercent = systemStorage
    ? Math.min(100, (systemStorage.totalMediaBytes / systemStorage.limitBytes) * 100)
    : 0;

  return (
    <section className="admin-panel" aria-labelledby="admin-panel-title">
      <div className="admin-panel-heading">
        <div>
          <p className="eyebrow">Yalnızca yönetici</p>
          <h3 id="admin-panel-title">Yönetici paneli</h3>
          <p>Kullanıcı kotalarını, proje alanını ve otomatik temizliği yönet.</p>
        </div>
        <span className="admin-badge">Yönetici</span>
      </div>

      {(maintenance || systemStorage) && (
        <div className="admin-maintenance-grid">
          {systemStorage && (
            <article className="admin-maintenance-card admin-system-quota">
              <span>Platform görsel depolaması</span>
              <strong>{formatStorage(systemStorage.totalMediaBytes)} / {formatStorage(systemStorage.limitBytes)}</strong>
              <progress value={systemMediaPercent} max="100" aria-label="Platform görsel kotası kullanım oranı" />
              <small>
                Taslak: {formatStorage(systemStorage.draftMediaBytes)} · Yayın kopyaları: {formatStorage(systemStorage.publicationMediaBytes)}
              </small>
              <label>
                Toplam görsel kotası (GB)
                <input
                  type="number"
                  min="0.01"
                  step="0.1"
                  value={systemLimitGib}
                  onChange={(event) => setSystemLimitGib(event.target.value)}
                  disabled={savingSystemLimit}
                />
              </label>
              <button className="primary-button" type="button" onClick={() => void saveSystemLimit()} disabled={savingSystemLimit}>
                {savingSystemLimit ? 'Kaydediliyor…' : 'Sistem kotasını kaydet'}
              </button>
              {!systemStorage.uploadsAllowed && (
                <small className="quota-over">
                  {systemStorage.blockedReason === 'PLATFORM_MEDIA_QUOTA_EXCEEDED'
                    ? 'Toplam kota dolu; yeni görsel yükleme tüm kullanıcılar için kapalı.'
                    : '10 GB güvenli disk payına ulaşıldı; yeni görsel yükleme tüm kullanıcılar için kapalı.'}
                </small>
              )}
            </article>
          )}
          {maintenance && <article className="admin-maintenance-card">
            <span>Odevlendir proje klasörü</span>
            <strong>{formatStorage(maintenance.storage.projectDirectoryBytes)} / {formatStorage(maintenance.storage.filesystemCapacityBytes)}</strong>
            <progress value={storagePercent} max="100" aria-label="Proje klasörünün disk kapasitesine oranı" />
            <small>Diskte {formatStorage(maintenance.storage.filesystemFreeBytes)} boş · Ölçüm {formatDate(maintenance.storage.measuredAt)}</small>
            {systemStorage && <small>Görsel yüklemeleri için ayrılan güvenlik payı: {formatStorage(systemStorage.minimumFreeDiskBytes)}</small>}
          </article>}
          {maintenance && <article className="admin-maintenance-card">
            <span>Son süreli içerik temizliği</span>
            <strong>{formatDate(maintenance.cleanup.lastCompletedAt)}</strong>
            <small>Son tur: {maintenance.cleanup.lastDeletedPublications} yayın, {maintenance.cleanup.lastDeletedResults} sonuç</small>
            {maintenance.cleanup.lastError && <small className="quota-over">Son hata: {maintenance.cleanup.lastError}</small>}
            <button className="ghost-button" type="button" onClick={() => void startCleanup()} disabled={cleaning || maintenance.cleanup.isRunning}>
              {cleaning || maintenance.cleanup.isRunning ? 'Temizlik çalışıyor…' : 'Şimdi güvenli temizlik yap'}
            </button>
          </article>}
        </div>
      )}

      {message && <p className="dashboard-message" role="status">{message}</p>}
      <div className="admin-defaults">
        Varsayılanlar: {formatStorage(defaultMediaLimitBytes)} görsel · {defaultPublicationLimit} aktif yayın
      </div>
      {loading && users.length === 0 ? (
        <p className="empty-drafts">Kullanıcılar yükleniyor…</p>
      ) : (
        <div className="admin-user-list">
          {users.map((user) => (
            <article className="admin-user-row" key={user.id}>
              <div className="admin-user-identity">
                <strong>{user.email}</strong>
                <span>{user.role === 'admin' ? 'Yönetici' : 'Oyun oluşturucu'} · {formatStorage(user.totalMediaBytes)} · {user.activePublications} aktif yayın</span>
                {user.totalMediaBytes > user.mediaLimitBytes && <span className="quota-over">Mevcut medya kullanımı kotayı aşıyor; yeni medya engellenir.</span>}
                {user.activePublications > user.activePublicationLimit && <span className="quota-over">Aktif yayın sayısı kotayı aşıyor; yeni yayın engellenir.</span>}
              </div>
              <label>
                Görsel kotası (MiB)
                <input type="number" min="1" max={Math.floor(maximumMediaLimitBytes / BYTES_PER_MIB)} step="1" value={mediaValues[user.id] ?? ''} onChange={(event) => setMediaValues((current) => ({ ...current, [user.id]: event.target.value }))} disabled={savingUserId === user.id} />
                <button className="text-button" type="button" onClick={() => void save(user, 'media')} disabled={!user.hasCustomMediaLimit || savingUserId === user.id}>Varsayılana dön</button>
              </label>
              <label>
                Aktif yayın kotası
                <input type="number" min="1" max="10000" step="1" value={publicationValues[user.id] ?? ''} onChange={(event) => setPublicationValues((current) => ({ ...current, [user.id]: event.target.value }))} disabled={savingUserId === user.id} />
                <button className="text-button" type="button" onClick={() => void save(user, 'publications')} disabled={!user.hasCustomActivePublicationLimit || savingUserId === user.id}>Varsayılana dön</button>
              </label>
              <div className="admin-user-actions">
                <button className="primary-button" type="button" onClick={() => void save(user)} disabled={savingUserId === user.id}>
                  {savingUserId === user.id ? 'Kaydediliyor…' : 'Kotaları kaydet'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
      {hasMore && <button className="ghost-button results-more" type="button" onClick={() => void loadMore()} disabled={loading}>Daha fazla kullanıcı göster</button>}
      <p className="admin-security-note">Yönetici rolü bu panelden veya kayıt ekranından verilemez; yalnız sunucudaki güvenli yönetici komutuyla atanır.</p>
    </section>
  );
}
